/**
 * ============================================================================
 * MASTER SERVICE: User Master Service (`tbl_user_master` - Sheet 1)
 * ============================================================================
 * Purpose:
 *   Handles API requests for managing platform users, roles, account links,
 *   and secure password updates.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import api from "../../core/services/api";
import { createCrudService } from "../../core/services/crudServiceFactory";

const baseUserService = createCrudService("users");

const userService = {
  ...baseUserService,

  /**
   * Securely updates or resets password for a specific user ID.
   *
   * @param {string|number} id - User ID or username
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} API response
   */
  changePassword: (id, newPassword) => {
    return api.post(`/users/${id}/change-password`, { newPassword });
  },

  /**
   * Retrieves dynamic queue configuration, 24-hour limit, and real-time usage stats.
   *
   * @param {string|number} id - User ID or username
   * @returns {Promise<Object>}
   */
  getQueueSettings: (id) => {
    return api.get(`/users/${id}/queue-settings`);
  },

  /**
   * Updates 24-hour messaging limit, rate limits (min/max), queue status, and worker config.
   *
   * @param {string|number} id - User ID or username
   * @param {Object} data - Queue and limit configuration
   * @returns {Promise<Object>}
   */
  updateQueueSettings: (id, data) => {
    return api.put(`/users/${id}/queue-settings`, data);
  },

  /**
   * Recreates or synchronizes dedicated RabbitMQ queue for user.
   *
   * @param {string|number} id - User ID or username
   * @returns {Promise<Object>}
   */
  recreateQueue: (id) => {
    return api.post(`/users/${id}/queue-recreate`);
  },

  /**
   * Executes queue lifecycle actions (PAUSE, RESUME, DRAIN, RETRY_FAILED, CLEAR_PENDING, RESTART_WORKERS).
   *
   * @param {string|number} id - User ID or username
   * @param {string} action - Action identifier
   * @returns {Promise<Object>}
   */
  executeQueueAction: (id, action) => {
    return api.post(`/users/${id}/queue-action`, { action });
  },
};

export default userService;
